import { useEffect } from "react";
import axios from "axios";
import {
  Sparkle, FileText, FileCheck, FileX, RotateCcw, Save, Download,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const READING_STATUS = {
  granted: "border-amber-500/40 bg-amber-500/10 text-amber-300",
  in_progress: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300",
  completed: "border-sky-500/40 bg-sky-500/10 text-sky-300",
  expired: "border-red-500/40 bg-red-500/10 text-red-300",
  superseded: "border-neutral-500/40 bg-neutral-500/10 text-neutral-400",
};
const READING_STATUS_LABEL = {
  granted: "Habilitada (sin iniciar)", in_progress: "En progreso",
  completed: "Completada", expired: "Expirada", superseded: "Reemplazada",
};

export const GrantReadingModal = ({ grantFor, setGrantFor, grantForm, setGrantForm, submitGrant }) => {
  const { authHeader } = useAuth();
  const DEFAULT_DRAWS = {
    normal: { spanish_cards: 3, tarot: 3 },
    complete: { spanish_cards: 4, tarot: 4, predictions: 1, additional: 1, questions_live: 1, runes: 2 },
  };
  const PHASE_LABELS = {
    spanish_cards: "🃏 Baraja Española", tarot: "🔮 Tarot",
    predictions: "⭐ Predicciones", additional: "🌙 Lectura Adicional",
    questions_live: "💬 Preguntas en vivo", runes: "ᚱ Runas",
  };
  const phases = DEFAULT_DRAWS[grantForm.reading_type] || {};
  const draws = grantForm.draws_per_phase || {};
  const setDraw = (k, v) => setGrantForm({
    ...grantForm,
    draws_per_phase: { ...(grantForm.draws_per_phase || {}), [k]: v },
  });

  // Pre-load saved defaults from backend whenever modal opens or type changes
  useEffect(() => {
    if (!grantFor) return;
    axios.get(`${API}/admin/settings/readings`, { headers: authHeader() })
      .then((r) => {
        const key = grantForm.reading_type === "complete" ? "default_draws_complete" : "default_draws_normal";
        const saved = r.data?.[key];
        if (saved && Object.keys(saved).length > 0 && Object.keys(draws).length === 0) {
          setGrantForm((g) => ({ ...g, draws_per_phase: saved }));
        }
      }).catch(() => null);
    // eslint-disable-next-line
  }, [grantFor, grantForm.reading_type]);

  const saveAsDefault = async () => {
    if (!Object.keys(draws).length) return toast.error("Configura las tiradas primero");
    try {
      const key = grantForm.reading_type === "complete" ? "default_draws_complete" : "default_draws_normal";
      await axios.put(`${API}/admin/settings/readings`, { [key]: draws }, { headers: authHeader() });
      toast.success("Valores guardados como predeterminados");
    } catch { toast.error("Error guardando"); }
  };

  const loadFactory = () => {
    setGrantForm({ ...grantForm, draws_per_phase: { ...phases } });
    toast.info("Valores de fábrica cargados");
  };

  return (
  <Dialog open={!!grantFor} onOpenChange={() => setGrantFor(null)}>
    <DialogContent className="bg-[#0a0a0a] border border-[#d4af37]/30 text-white max-w-lg max-h-[90vh] overflow-y-auto">
      <DialogHeader><DialogTitle className="font-serif flex items-center gap-2"><Sparkle className="w-5 h-5 text-purple-300" /> Habilitar Lectura Online</DialogTitle></DialogHeader>
      <p className="text-xs text-neutral-400">Usuario: <span className="font-mono text-[#d4af37]">{grantFor}</span></p>
      <div className="space-y-3">
        <div>
          <Label className="text-neutral-300 mb-1.5 text-xs uppercase tracking-widest">Tipo de Lectura</Label>
          <Select value={grantForm.reading_type} onValueChange={(v) => setGrantForm({...grantForm, reading_type: v, draws_per_phase: {}})}>
            <SelectTrigger data-testid="grant-type" className="bg-black/40 border-white/10 text-white"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#0a0a0a] border-[#d4af37]/30 text-white">
              <SelectItem value="normal">Lectura Normal (Baraja + Tarot) — 20 min</SelectItem>
              <SelectItem value="complete">Lectura Completa con Runas — 60 min</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-neutral-300 mb-1.5 text-xs uppercase tracking-widest">Duración (min, opcional)</Label>
          <Input data-testid="grant-duration" type="number" min="5" max="180" value={grantForm.duration_minutes} onChange={(e) => setGrantForm({...grantForm, duration_minutes: e.target.value})} placeholder="Por defecto 20 / 60" className="bg-black/40 border-white/10 text-white" />
        </div>

        <div className="border border-[#d4af37]/20 rounded-lg p-3 bg-black/30">
          <div className="flex items-center justify-between mb-2">
            <Label className="text-[#d4af37] text-xs uppercase tracking-widest">Tiradas por fase (1–8)</Label>
            <div className="flex gap-1">
              <button type="button" data-testid="grant-load-factory" onClick={loadFactory} title="Cargar valores de fábrica" className="text-[10px] px-2 py-0.5 rounded border border-white/10 text-neutral-300 hover:text-[#d4af37] inline-flex items-center gap-1"><Download className="w-2.5 h-2.5" /> Fábrica</button>
              <button type="button" data-testid="grant-save-default" onClick={saveAsDefault} title="Guardar como predeterminado para futuras lecturas" className="text-[10px] px-2 py-0.5 rounded border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10 inline-flex items-center gap-1"><Save className="w-2.5 h-2.5" /> Guardar default</button>
            </div>
          </div>
          <div className="space-y-2">
            {Object.entries(phases).map(([k, defaultVal]) => (
              <div key={k} className="flex items-center justify-between gap-3">
                <span className="text-xs text-neutral-300 flex-1">{PHASE_LABELS[k] || k}</span>
                <Input
                  data-testid={`grant-draws-${k}`}
                  type="number" min="1" max="8"
                  value={draws[k] ?? defaultVal}
                  onChange={(e) => setDraw(k, Math.max(1, Math.min(8, parseInt(e.target.value) || 1)))}
                  className="bg-black/40 border-white/10 text-white w-20 h-8 text-center"
                />
                <span className="text-[10px] text-neutral-500 w-12">def: {defaultVal}</span>
              </div>
            ))}
          </div>
        </div>

        <div>
          <Label className="text-neutral-300 mb-1.5 text-xs uppercase tracking-widest">Personalización (instrucción al IA)</Label>
          <Textarea data-testid="grant-focus" rows={3} value={grantForm.custom_focus} onChange={(e) => setGrantForm({...grantForm, custom_focus: e.target.value})} placeholder="Ej: Enfocar en relación de pareja y bloqueos kármicos del padre" className="bg-black/40 border-white/10 text-white" />
        </div>
        <button data-testid="grant-submit" onClick={submitGrant} className="btn-gold w-full py-3 rounded-full uppercase text-xs tracking-widest">Habilitar Lectura</button>
      </div>
    </DialogContent>
  </Dialog>
  );
};

export const ProfileReviewModal = ({ reviewReq, setReviewReq, reviewApproved, setReviewApproved, reviewNote, setReviewNote, submitReview }) => (
  <Dialog open={!!reviewReq} onOpenChange={() => setReviewReq(null)}>
    <DialogContent className="bg-[#0a0a0a] border border-[#d4af37]/30 text-white max-w-lg">
      <DialogHeader><DialogTitle className="font-serif">Revisar cambios de perfil</DialogTitle></DialogHeader>
      {reviewReq && (
        <div className="space-y-3">
          <p className="text-xs text-neutral-400">Usuario: <span className="font-mono text-[#d4af37]">{reviewReq.user_visible_id}</span> · {reviewReq.user_name}</p>
          <div className="space-y-2">
            {Object.entries(reviewReq.requested_changes || {}).map(([k, v]) => (
              <div key={k} className="flex items-start gap-3 bg-black/40 border border-white/10 rounded p-3">
                <input type="checkbox" data-testid={`review-${k}`} checked={reviewApproved[k] || false} onChange={(e) => setReviewApproved({...reviewApproved, [k]: e.target.checked})} className="mt-1 accent-[#d4af37]" />
                <div className="flex-1 text-xs">
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">{k}</div>
                  <div className="text-neutral-400 line-through mb-0.5">{reviewReq.current_values?.[k] || "—"}</div>
                  <div className="text-emerald-300">{v}</div>
                </div>
              </div>
            ))}
          </div>
          <Textarea data-testid="review-note" rows={2} value={reviewNote} onChange={(e) => setReviewNote(e.target.value)} placeholder="Nota para el usuario (opcional)" className="bg-black/40 border-white/10 text-white" />
          <button data-testid="review-submit" onClick={submitReview} className="btn-gold w-full py-3 rounded-full uppercase text-xs tracking-widest">Aplicar cambios seleccionados</button>
        </div>
      )}
    </DialogContent>
  </Dialog>
);

export const ReadingDetailModal = ({ openReading, setOpenReading, resetReading }) => (
  <Dialog open={!!openReading} onOpenChange={() => setOpenReading(null)}>
    <DialogContent className="bg-[#0a0a0a] border border-[#d4af37]/30 text-white max-w-2xl max-h-[85vh] overflow-y-auto">
      <DialogHeader><DialogTitle className="font-serif flex items-center gap-2"><Sparkle className="w-5 h-5 text-purple-300" /> Resumen de Lectura</DialogTitle></DialogHeader>
      {openReading && (
        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div><span className="text-neutral-500">Usuario:</span> <span className="font-mono text-[#d4af37]">{openReading.user_visible_id}</span> · {openReading.user_name}</div>
            <div><span className="text-neutral-500">Tipo:</span> {openReading.reading_label} ({openReading.duration_minutes}m)</div>
            <div><span className="text-neutral-500">Estado:</span> <span className={`px-1.5 py-0.5 rounded-full border ${READING_STATUS[openReading.status]}`}>{READING_STATUS_LABEL[openReading.status]}</span></div>
            <div><span className="text-neutral-500">Iniciada:</span> {openReading.started_at ? new Date(openReading.started_at).toLocaleString() : "—"}</div>
          </div>
          {openReading.custom_focus && <div className="text-xs bg-purple-500/10 border border-purple-500/30 rounded p-2 text-purple-200">🎯 {openReading.custom_focus}</div>}
          {openReading.form_data && (
            <div className="text-xs bg-black/40 border border-white/10 rounded p-3 space-y-0.5">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Datos del Cliente</div>
              <div><b>Nombre:</b> {openReading.form_data.full_name}</div>
              <div><b>Nacimiento:</b> {openReading.form_data.dob}</div>
              <div><b>Residencia:</b> {openReading.form_data.location}</div>
              {openReading.form_data.children_info && <div><b>Hijos:</b> {openReading.form_data.children_info}</div>}
              {openReading.form_data.partner_info && <div><b>Pareja:</b> {openReading.form_data.partner_info}</div>}
            </div>
          )}
          {openReading.questions?.length > 0 && (
            <div className="text-xs">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Preguntas ({openReading.questions.length})</div>
              <ul className="space-y-1 text-neutral-300 list-disc list-inside">
                {openReading.questions.map((q, i) => <li key={i}>{q}</li>)}
              </ul>
            </div>
          )}
          {openReading.events?.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">Eventos de la lectura ({openReading.events.length})</div>
              <div className="space-y-2">
                {openReading.events.map((ev) => (
                  <div key={ev.id} className="bg-black/40 border border-purple-500/20 rounded p-3 text-xs">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-purple-300 font-semibold">{ev.kind}</span>
                      <span className="text-[10px] text-neutral-500">{new Date(ev.at).toLocaleTimeString()}</span>
                    </div>
                    {ev.items?.length > 0 && (
                      <div className="text-[#d4af37] italic mb-1.5">{ev.items.join(" · ")}</div>
                    )}
                    <div className="text-neutral-200 whitespace-pre-wrap">{ev.interpretation}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="flex gap-2 pt-2 border-t border-white/10">
            <button onClick={() => resetReading(openReading.id)} className="text-xs px-3 py-1.5 rounded border border-amber-500/30 text-amber-300 hover:bg-amber-500/10 flex items-center gap-1"><RotateCcw className="w-3 h-3" /> Reiniciar</button>
          </div>
        </div>
      )}
    </DialogContent>
  </Dialog>
);

export const ReceiptPreviewModal = ({ openReceipt, setOpenReceipt, setReceiptStatus }) => (
  <Dialog open={!!openReceipt} onOpenChange={() => setOpenReceipt(null)}>
    <DialogContent className="bg-[#0a0a0a] border border-[#d4af37]/30 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
      <DialogHeader><DialogTitle className="font-serif flex items-center gap-2"><FileText className="w-5 h-5 text-[#d4af37]" /> Comprobante: {openReceipt?.plan_name}</DialogTitle></DialogHeader>
      {openReceipt && (
        <div className="space-y-3">
          <div className="text-xs text-neutral-400">{openReceipt.filename}</div>
          {openReceipt.mime_type?.startsWith("image/") ? (
            <img src={`data:${openReceipt.mime_type};base64,${openReceipt.data_b64}`} alt="comprobante" className="max-w-full rounded border border-white/10" />
          ) : openReceipt.mime_type === "application/pdf" ? (
            <iframe src={`data:application/pdf;base64,${openReceipt.data_b64}`} title="pdf" className="w-full h-[60vh] rounded border border-white/10" />
          ) : (
            <div className="text-neutral-400">Formato no previsualizable</div>
          )}
          <div className="flex gap-2 pt-2 border-t border-white/10">
            <button data-testid="receipt-confirm" onClick={() => setReceiptStatus(openReceipt.plan_id, openReceipt.receipt_id, "confirmed")} className="text-xs px-3 py-1.5 rounded border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 flex items-center gap-1"><FileCheck className="w-3 h-3" /> Confirmar pago</button>
            <button data-testid="receipt-reject" onClick={() => setReceiptStatus(openReceipt.plan_id, openReceipt.receipt_id, "rejected")} className="text-xs px-3 py-1.5 rounded border border-red-500/40 text-red-300 hover:bg-red-500/10 flex items-center gap-1"><FileX className="w-3 h-3" /> Rechazar</button>
          </div>
        </div>
      )}
    </DialogContent>
  </Dialog>
);
